import { useState, useCallback, useMemo } from 'react'
import { useQueries, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import { useRestaurant } from '@/contexts/RestaurantContext'
import { useGeneralSettings } from '@/features/general-settings/hooks/useGeneralSettings'
import { useCategories } from '@/features/menu-editor/hooks/useCategories'
import { useItems } from '@/features/menu-editor/hooks/useItems'
import { useLabelSettings } from './useLabelSettings'
import { resolvePlaceAddress } from '@/features/maps/hooks/useResolvedPlaces'
import { buildReceipt } from '../utils/receiptFormatter'
import { toDantSuChunks, getNativeQrCode } from '../utils/receiptRenderers'
import { DEFAULT_LABEL_SETTINGS } from '../utils/receiptTypes'
import type { ReceiptElement, ReceiptRestaurantInfo, ReceiptMenuItemInfo, LabelSettings } from '../utils/receiptTypes'
import type { DeepOrder } from '../types'
import { API_BASE_URL } from '@/api/config'
import { apiClient } from '@/api/client'
import { trackPrint } from '@/lib/deviceDiagnostics'

/** Whether the current platform supports native printing */
export const canPrint = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'

// ponytail: logo cached for the app's lifetime (like the old app's bitmap cache); a changed logo prints after an app restart.
const logoHexCache = new Map<string, Promise<string | undefined>>()

function restaurantIconUrl(restaurantId: number): string {
  return new URL(`/restaurantIcon/${restaurantId}`, API_BASE_URL || window.location.origin).toString()
}

// Option group labels: the old app printed each variation category's displayName, fetched by id and cached.
function variationCategoryQuery(id: number) {
  return {
    queryKey: ['receipt-variation-category', id],
    queryFn: async () => {
      const { data } = await apiClient.get<{ displayName?: string | null }>('/variationCategory', { params: { id } })
      return data.displayName ?? null
    },
    staleTime: 10 * 60 * 1000,
  }
}

function variationCategoryIds(order: DeepOrder): number[] {
  return [...new Set(order.orderItems.flatMap(item => item.variations.map(v => v.menuItemVariationCategoryId)))]
}

async function fetchVariationCategoryLabels(queryClient: QueryClient, order: DeepOrder): Promise<Map<number, string | null>> {
  const ids = variationCategoryIds(order)
  // An unreachable label only drops that group heading; it never blocks the ticket.
  const labels = await Promise.all(ids.map(id => queryClient.fetchQuery(variationCategoryQuery(id)).catch(() => null)))
  return new Map(ids.map((id, index) => [id, labels[index]]))
}

/** Option group labels for the receipt preview (same cache as printing). */
export function useVariationCategoryLabels(order: DeepOrder): Map<number, string | null> {
  const ids = useMemo(() => variationCategoryIds(order), [order])
  const results = useQueries({ queries: ids.map(variationCategoryQuery) })
  return new Map(ids.map((id, index) => [id, results[index]?.data ?? null]))
}

/** The restaurant icon as DantSu image hex, converted natively exactly like the old RestaurantApp. */
function loadLogoHex(restaurantId: number, settings: LabelSettings): Promise<string | undefined> {
  const key = [restaurantId, settings.printerDpi, settings.printerWidthMm, settings.printerNbrCharactersPerLine].join(':')
  let hex = logoHexCache.get(key)
  if (!hex) {
    hex = import('@/plugins/escpos-printer')
      .then(({ EscPosPrinter }) => EscPosPrinter.imageToHex({
        url: restaurantIconUrl(restaurantId),
        printerDpi: settings.printerDpi,
        printerWidthMM: settings.printerWidthMm,
        printerNbrCharactersPerLine: settings.printerNbrCharactersPerLine,
        printerCharsetName: settings.printerCharsetName,
        printerCharsetCommand: settings.printerCharsetCommand,
      }))
      .then((result) => result.hex)
      .catch((error: unknown) => {
        // Same as the old app: a missing logo never blocks the ticket.
        console.warn('Receipt logo unavailable, printing without it', error)
        logoHexCache.delete(key)
        return undefined
      })
    logoHexCache.set(key, hex)
  }
  return hex
}

export function usePrintOrder() {
  const { selectedRestaurant } = useRestaurant()
  const restaurantId = selectedRestaurant?.id ?? 0
  const queryClient = useQueryClient()

  const { settings: generalSettings } = useGeneralSettings(restaurantId)
  const { data: labelSettings, isLoading: labelSettingsLoading } = useLabelSettings(restaurantId)
  const { categories, isLoading: categoriesLoading } = useCategories(restaurantId)
  // Pass null categoryId to get all items for the restaurant
  const { items: allMenuItems, isLoading: itemsLoading } = useItems(restaurantId, null)

  const [isPrinting, setIsPrinting] = useState(false)

  // Build category name lookup
  const categoryMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const cat of categories) {
      map.set(cat.id, cat.name ?? '')
    }
    return map
  }, [categories])

  // Build menu item info for receipt
  const menuItemsInfo: ReceiptMenuItemInfo[] = useMemo(() => {
    return allMenuItems.map(item => ({
      id: item.id,
      menuitemcategoryId: item.menuitemcategoryId,
      categoryName: categoryMap.get(item.menuitemcategoryId) ?? '',
      menuposition: item.menuposition ?? 0,
      taxRate: item.taxRate ?? null,
      description: item.description ?? null,
    }))
  }, [allMenuItems, categoryMap])

  // Build restaurant info
  const restaurantInfo: ReceiptRestaurantInfo | null = useMemo(() => {
    if (!generalSettings) return null
    return {
      name: generalSettings.name ?? '',
      streetName: generalSettings.streetName ?? '',
      houseNumber: generalSettings.houseNumber ?? '',
      postcode: generalSettings.postcode ?? '',
      city: generalSettings.city ?? '',
      phoneNumber: generalSettings.phoneNumber ?? '',
      defaultTaxRate: generalSettings.defaultTaxRate ?? 0,
      currency: generalSettings.currency,
      language: generalSettings.language,
    }
  }, [generalSettings])

  /**
   * Build receipt elements for an order.
   * Can be used for both printing and preview.
   */
  const buildReceiptElements = useCallback((
    order: DeepOrder,
    resolvedAddress?: string,
    extras: { logoHex?: string; variationCategoryLabels?: ReadonlyMap<number, string | null>; preview?: boolean } = {},
  ): ReceiptElement[] | null => {
    if (!restaurantInfo) return null

    return buildReceipt({
      order,
      restaurant: restaurantInfo,
      labelSettings: labelSettings ?? DEFAULT_LABEL_SETTINGS,
      menuItems: menuItemsInfo,
      logoHex: extras.logoHex,
      logoSrc: extras.preview ? restaurantIconUrl(restaurantId) : undefined,
      resolvedAddress,
      variationCategoryLabels: extras.variationCategoryLabels,
    })
  }, [restaurantInfo, labelSettings, menuItemsInfo, restaurantId])

  /**
   * Print an order immediately (Android only).
   */
  const printOrder = useCallback(async (
    order: DeepOrder,
    resolvedAddress?: string,
    auto = false,
  ): Promise<void> => {
    if (!canPrint) throw new Error('Printing not supported on this platform')

    const settings = labelSettings ?? DEFAULT_LABEL_SETTINGS
    const copies = parseInt(localStorage.getItem('printer_copies') ?? '1', 10)

    setIsPrinting(true)
    try {
      await trackPrint('order', { orderId: order.id, auto, copies }, async () => {
        // RestaurantApp resolves the stored Place ID before building the
        // receipt. Do the same here so an early print tap cannot print the raw
        // Google Place ID while the React lookup is still in flight.
        const printAddress = resolvedAddress ?? (
          order.withDelivery && order.deliveryAddress?.city
            ? (await resolvePlaceAddress(order.deliveryAddress.city)) ?? undefined
            : undefined
        )

        if (order.withDelivery && !order.dinein && order.deliveryAddress && !printAddress) {
          throw new Error('Delivery address could not be resolved')
        }

        const [logoHex, variationCategoryLabels] = await Promise.all([
          settings.printLogo ? loadLogoHex(restaurantId, settings) : undefined,
          fetchVariationCategoryLabels(queryClient, order),
        ])
        const elements = buildReceiptElements(order, printAddress, { logoHex, variationCategoryLabels })
        if (!elements) throw new Error('Restaurant info not loaded yet')

        const chunks = toDantSuChunks(elements, settings.printerNbrCharactersPerLine)
        const nativeQrCode = getNativeQrCode(elements)

        // Dynamic import to avoid loading native plugin on web
        const { EscPosPrinter } = await import('@/plugins/escpos-printer')

        const storedAddress = localStorage.getItem('printer_device_address') ?? undefined

        await EscPosPrinter.printFormattedText({
          deviceAddress: storedAddress,
          textChunks: chunks,
          printerDpi: settings.printerDpi,
          printerWidthMM: settings.printerWidthMm,
          printerNbrCharactersPerLine: settings.printerNbrCharactersPerLine,
          printerCharsetName: settings.printerCharsetName,
          printerCharsetCommand: settings.printerCharsetCommand,
          copies,
          nativeQrCode,
        })
      })
    } finally {
      setIsPrinting(false)
    }
  }, [buildReceiptElements, labelSettings, queryClient, restaurantId])

  return {
    canPrint,
    // Everything a receipt needs has loaded, so an automatic print matches a manual one.
    isReady: !!restaurantInfo && !labelSettingsLoading && !categoriesLoading && !itemsLoading,
    isPrinting,
    printOrder,
    buildReceiptElements,
  }
}
